import React, { useState, useEffect, useContext } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Modal, Form, Button } from 'semantic-ui-react'
import { ORG_SIZE } from '../../constants/organization'
import {
  CREATE_ORGANIZATION,
  GET_ORG_TYPES,
  UPDATE_ORGANIZATION,
} from '../../graphql/organization'
import MessageContext from '../../contexts/MessageContext'
import DataContext from '../../contexts/DataContext'

const NewOrganizationModal = ({ defaultQuery, done, open, onClose }) => {
  const { setAlertMsg } = useContext(MessageContext)
  const { countries } = useContext(DataContext)
  const { data: orgTypesData } = useQuery(GET_ORG_TYPES)
  const [name, setName] = useState('')
  const [organizationId, setOrganizationId] = useState(null)
  const [size, setSize] = useState('')
  const [type, setType] = useState('')
  const [country, setCountry] = useState('')
  const [city, setCity] = useState('')
  const [url, setUrl] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [formError, setFormError] = useState({})

  useEffect(() => {
    // Updates the search bar default value based on organization autocomplete query
    setName(defaultQuery)
  }, [defaultQuery])

  const [createOrganization, { loading: creating }] = useMutation(
    CREATE_ORGANIZATION,
    {
      context: { skipGlobalErrorHandling: true },
      update: (cache, { data: { createOrganization } }) => {
        cache.modify({
          fields: {
            autocomplete: (previous, { toReference }) => {
              return [...previous, toReference(createOrganization)]
            },
          },
        })
      },
      onCompleted: ({ createOrganization }) => {
        const { id, name, location } = createOrganization
        done(`${name} - ${location.city}, ${location.country}`, id)
        clearFieldsAndClose()
      },
      onError: (error) => {
        setAlertMsg({
          title: 'Error',
          content: error.message,
        })
        onClose()
      },
    }
  )

  const [updateOrganization, { loading: updating }] = useMutation(
    UPDATE_ORGANIZATION,
    {
      context: { skipGlobalErrorHandling: true },
      update: (cache, { data: { updateOrganization } }) => {
        cache.modify({
          fields: {
            autocomplete: (previous, { toReference }) => {
              return [...previous, toReference(updateOrganization)]
            },
          },
        })
      },
      onCompleted: ({ updateOrganization }) => {
        const { id, name, location } = updateOrganization
        done(`${name} - ${location.city}, ${location.country}`, id)
        clearFieldsAndClose()
      },
      onError: (error) => {
        setAlertMsg({
          title: 'Error',
          content: error.message,
        })
        onClose()
      },
    }
  )

  const clearFieldsAndClose = () => {
    setName(defaultQuery)
    setOrganizationId(null)
    setSize('')
    setType('')
    setCountry('')
    setCity('')
    setUrl('')
    setErrorMsg('')
    setFormError({})
    onClose()
  }

  const hasEmptyFields = () => {
    formError.name = !name
    formError.size = !size
    formError.type = !type
    formError.country = !country
    formError.city = !city
    setFormError(formError)
    return Object.values(formError).includes(true)
  }

  const isValidUrl = (urlString) => {
    if (!urlString) return true // URL is optional
    try {
      const url = new URL(urlString)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch {
      return false
    }
  }

  const onCreate = () => {
    setErrorMsg('')
    if (hasEmptyFields()) {
      setErrorMsg('Please fill in all the required fields')
      return
    }
    if (!isValidUrl(url)) {
      setErrorMsg(
        'Please enter a valid URL (must start with http:// or https://)'
      )
      return
    }
    const ORGANIZATION_INPUT = {
      name,
      size,
      type,
      location: { city, country },
      url: url || undefined,
    }
    if (organizationId) {
      updateOrganization({
        variables: {
          organizationId: organizationId,
          organization: ORGANIZATION_INPUT,
        },
      })
    } else {
      createOrganization({
        variables: {
          organization: ORGANIZATION_INPUT,
        },
      })
    }
  }

  return (
    <Modal closeIcon open={open} onClose={clearFieldsAndClose}>
      <Modal.Header>Add a new organization</Modal.Header>
      <Modal.Content>
        <i style={{ color: 'red' }}>{errorMsg}</i>
        <Form>
          <Form.Group widths="equal">
            <Form.Input
              error={formError?.name}
              label="Name"
              placeholder="Enter organization name"
              readOnly={organizationId}
              value={name}
              onChange={(e, { value }) => {
                setName(value)
                setOrganizationId(null)
              }}
            />
            <Form.Dropdown
              error={formError.size}
              selection
              label="Organization size (# FTEs)"
              placeholder="Select organization's size"
              options={ORG_SIZE.map((size) => ({
                key: size,
                value: size,
                text: size,
              }))}
              value={size}
              onChange={(e, { value }) => setSize(value)}
            />
          </Form.Group>
          <Form.Dropdown
            error={formError.type}
            search
            selection
            label="Type"
            placeholder="Enter organization type ( e.g, Biopharma, University, Academic Medical Center )"
            options={(orgTypesData?.orgTypes ?? []).map((type) => ({
              key: type,
              value: type,
              text: type,
            }))}
            value={type}
            onChange={(e, { value }) => setType(value)}
          />
          <Form.Group widths="equal">
            <Form.Dropdown
              error={formError.country}
              search
              selection
              label="Country"
              placeholder="Select organization's country"
              options={countries.map((c) => ({
                key: c.name,
                value: c.name,
                text: c.name,
              }))}
              value={country}
              onChange={(e, { value }) => setCountry(value)}
            />
            <Form.Input
              error={formError.city}
              label="City"
              placeholder="Enter organization's city"
              value={city}
              onChange={(e, { value }) => setCity(value)}
            />
          </Form.Group>
          <Form.Input
            label="Organization URL (Optional)"
            placeholder="https://www.example.org"
            value={url}
            onChange={(e, { value }) => setUrl(value)}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button content="Cancel" onClick={clearFieldsAndClose} />
        <Button
          primary
          loading={creating || updating}
          disabled={creating || updating}
          content="Add"
          onClick={onCreate}
        />
      </Modal.Actions>
    </Modal>
  )
}

export default NewOrganizationModal
